import AuthLayout from "./AuthLayout";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Button } from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import OTPInput from "react-otp-input";
import { useState, useEffect } from "react";
import { usePostVerifyOtpMutation, useSendOtpMutation, usePostForgotPasswordMutation, useSendOtpToVerifyMutation } from "../../service/auth";
import { OTP_TYPE } from "../../utils/enums";
import { toast } from "react-toastify";
import Loader from "../../constants/Loader";
import { useAppDispatch } from "../../hooks/store";
import { setCredentials } from "../../reducers/authSlice";
import { setToStorage, STORAGE_KEYS } from "../../helpers";

const OtpVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [otp, setOtp] = useState<string>("");
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const email = location.state?.email;
  const mobileNumber = location.state?.mobileNumber;
  const countryCode = location.state?.countryCode;

  const [verifyOtp, { isLoading }] = usePostVerifyOtpMutation();
  const [sendOtp] = useSendOtpMutation();
  const [forgotPassword] = usePostForgotPasswordMutation();
  const [sendOtpToVerify] = useSendOtpToVerifyMutation();
  const dispatch = useAppDispatch();

  const [timer, setTimer] = useState(60);

  useEffect(() => {
    let interval: any;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleResend = async () => {
    let payload: any = {};
    if (email) {
      payload.email = email;
    } else {
      payload.phone = mobileNumber;
      payload.countryCode = countryCode;
    }

    try {
      let response;
      if (type === "forgotPassword") {
        payload.otpType = 3;
        response = await forgotPassword(payload).unwrap();
      } else if (type === "signup") {
        payload.otpType = OTP_TYPE.SINGNUP;
        response = await sendOtp(payload).unwrap();
      } else if (type === "signup-form") {
        response = await sendOtpToVerify(payload).unwrap();
      } else {
        payload.otpType = OTP_TYPE.LOGIN;
        response = await sendOtp(payload).unwrap();
      }

      if (response.statusCode === 200 || response.statusCode === 201) {
        toast.success("OTP resent successfully");
        setTimer(30); // Start timer again
      } else {
        toast.error(response.message || "Failed to resend OTP");
      }
    } catch (error: any) {
      toast.error(error?.data?.message || "An error occurred");
      console.error(error);
    }
  };

  // Conditionally define the text
  const otpText =
    mobileNumber || countryCode
      ? `A verification OTP has been sent to ${countryCode} ${mobileNumber}. Please check your messages.`
      : `A verification OTP has been sent to ${email}. Please check your inbox.`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length < 4) {
      toast.error("Please enter 4-digit OTP");
      return;
    }

    let otpType: number = OTP_TYPE.LOGIN;
    if (type === "signup") otpType = OTP_TYPE.SINGNUP;
    if (type === "forgotPassword") otpType = OTP_TYPE.FORGOT;

    const payload: any = {
      otp: otp,
      otpType,
    };

    if (email) {
      payload.email = email;
    } else {
      payload.phone = mobileNumber;
      payload.countryCode = countryCode;
    }

    try {
      const response = await verifyOtp(payload).unwrap();
      if (response.statusCode === 200 || response.statusCode === 201) {
        toast.success(response.message || "OTP verified successfully");

        if (type === "phoneLogin") {
          if (response.data) {
            const { token, ...user } = response.data as any;
            dispatch(setCredentials({ user, token }));
            if (token) {
              setToStorage(STORAGE_KEYS.token, token);
            }

            if ((response.data as any).readyToLogin) {
              navigate("/dashboard");
            } else {
              navigate("/signup-form");
            }
          } else {
            navigate("/signup-form");
          }
        } else if (type === "forgotPassword") {
          navigate("/reset-password", {
            state: { email, mobileNumber, countryCode },
          });
        } else if (type === "signup") {
          const { token, ...user } = response.data as any;
          dispatch(setCredentials({ user, token }));
          if (token) {
            setToStorage(STORAGE_KEYS.token, token);
          }
          navigate("/signup-form", {
            state: { email, mobileNumber, countryCode },
          });
        }
      } else {
        toast.error(response.message || "Verification failed");
        setOtp("");
      }
    } catch (error: any) {
      setOtp("");
      toast.error(error?.data?.message || "Something went wrong");
    }
  };

  const getTitle = () => {
    if (type === "forgotPassword") return "Forgot Password";
    if (type === "phoneLogin") return "OTP";
    if (type === "signup") return "Signup";
    return "";
  };

  return (
    <AuthLayout>
      <Loader isLoad={isLoading} />
      <div className="authBox">
        <h2>Verify {getTitle()}</h2> <p>{otpText}</p>
        <form className="form">
          <div className="control_group">
            <div className="opt_fields">
              <OTPInput
                value={otp}
                onChange={setOtp}
                numInputs={4}
                renderInput={(props) => <input {...props} id="otp" />}
                inputType="tel"
                shouldAutoFocus
              />
            </div>
            <div className="auth_bottom">
              <p>
                <button
                  type="button"
                  className="link"
                  onClick={handleResend}
                  disabled={timer > 0}
                  style={{ pointerEvents: timer > 0 ? "none" : "auto", opacity: timer > 0 ? 0.6 : 1 }}
                >
                </button>
                {timer > 0 ? `Resend OTP (${timer}s)` : "Resend OTP"}
              </p>
            </div>
          </div>
          <div className="form_btn">
            <Button
              type="submit"
              className="btn btn_primary w_100"
              onClick={handleSubmit}
              disabled={isLoading || timer === 0}
            >
              {isLoading ? "Verifying..." : "Submit"}
            </Button>
          </div>
        </form>
        <div className="auth_bottom">
          <p>
            <button className="link" onClick={() => navigate("/")}>
              <KeyboardBackspaceIcon /> Back to Login
            </button>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
};

export default OtpVerification;
